import { createFileRoute } from "@tanstack/react-router";
import { isValidElement, lazy, Suspense, useState, type ReactNode } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Faq } from "@/components/site/Faq";
import playButton from "@/assets/restore/play.png.asset.json";
import { primaryVideoObject } from "@/lib/video-seo";
import { PosterVideo } from "@/components/site/PosterVideo";
import { safeJsonLd } from "@/lib/structured-data-core";
import { absoluteUrl } from "@/lib/absolute-url";
import { GROUPS } from "@/lib/faq-data";

const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));

const TITLE = "Non-Surgical Hair Loss Solutions | EVERHAIR by RESTORE®";
const DESCRIPTION =
  "EVERHAIR is a non-surgical hair growth program: low-level laser therapy, prescription-strength medication, shampoo and physician monitoring.";
const HERO_LOGO = "/img/r/wp-2025-02-everhair-tag-blk-alpha.webp";
const HERO_BG = "/img/r/wp-2025-03-non-surgical-banner-3-1.webp";
const HERO_VIDEO =
  "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/EverHair_2022Commercial_60s_V1.mp4";
const BEFORE_IMG = "/img/r/wp-2021-11-matt-d-front-before-restore-1.webp";
const AFTER_IMG = "/img/r/wp-2021-11-matt-d-front-after-restore-1.webp";


// This page used the default hair-transplant FAQs. Show the non-surgical
// questions already published on /hair-transplant-faqs/ instead.
const NON_SURGICAL_QUESTIONS = [
  "How can I make my hair thicker?",
  "What is Minoxidil?",
  "What is Finasteride?",
  "Does Rogaine work?",
  "Does Propecia work?",
  "What are Finasteride / Propecia side effects?",
  "What are Minoxidil / Rogaine side effects?",
  "Is Propecia a replacement for a hair transplant?",
];
const answerText = (a: ReactNode): string =>
  typeof a === "string" ? a : isValidElement<{ children?: ReactNode }>(a) ? answerText(a.props.children) : "";
const nonSurgicalFaqs = (GROUPS.find((g) => g.title === "Non-Surgical Hair FAQ's")?.items ?? [])
  .filter((f) => NON_SURGICAL_QUESTIONS.includes(f.q))
  .map((f) => ({ q: f.q, a: answerText(f.a) }))
  .filter((f) => f.a);

const benefits = [
  { icon: "/img/r/wp-2021-11-stop-icon1.webp", alt: "Stop Shedding Icon", label: "Stop Shedding" },
  { icon: "/img/r/wp-2021-11-stop-icon2.webp", alt: "Increase Thickness Icon", label: "Increase thickness of thinning hair" },
  { icon: "/img/r/wp-2021-11-stop-icon3.webp", alt: "Promote Hair Growth Icon", label: "Promote hair growth" },
];

const program = [
  {
    icon: "/img/r/wp-2021-11-d-icon-1-1.webp",
    alt: "Laser Therapy Icon",
    title: "Laser Therapy",
    body:
      "Low-level laser therapy (LLLT) stimulates stem cells at the base of hair follicles. You'll have use of a doctor-approved, low-level laser therapy (LLLT) device that helps to prevent future hair loss and also promotes hair growth.",
  },
  {
    icon: "/img/r/wp-2021-11-d-icon-2-1.webp",
    alt: "Non surgical prescriptions",
    title: "Prescription-Strength Medications and Shampoo",
    body:
      "You'll get a personalized, doctor-prescribed regimen of oral and topical medications to fight male-pattern baldness, along with specially formulated anti-inflammatory shampoo.",
  },
  {
    icon: "/img/r/wp-2021-11-d-icon-3-1.webp",
    alt: "Office Visit",
    title: "All Office Visits",
    body: "Your program will be managed and progress monitored by a RESTORE® physician.",
  },
];

export const Route = createFileRoute("/non-surgical")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://restorehair.com/non-surgical/" },
      { property: "og:image", content: absoluteUrl(AFTER_IMG) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absoluteUrl(AFTER_IMG) },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/non-surgical/" }],
    scripts: [{
      type: "application/ld+json",
      children: safeJsonLd(primaryVideoObject("/non-surgical/")),
    }],
  }),
  component: NonSurgicalPage,
});

function HeroContent({
  setVideoOpen,
  headingTag: HeadingTag = "p",
}: {
  setVideoOpen: (open: boolean) => void;
  headingTag?: "h1" | "p";
}) {
  return (
    <>
      <img
        src={HERO_LOGO}
        alt="EVERHAIR by RESTORE"
        className="h-16 w-auto object-contain md:h-24"
      />
      <HeadingTag className="mt-8 text-3xl leading-tight tracking-tight text-foreground md:text-5xl">
        Grow thicker, fuller hair
        <br className="hidden md:block" /> without surgery or downtime.
      </HeadingTag>
      <button
        type="button"
        onClick={() => setVideoOpen(true)}
        aria-label="Play the EVERHAIR video"
        title="Play Video"
        className="mt-8 block transition hover:opacity-90"
      >
        <img
          src={playButton.url}
          alt="Play video"
          className="h-20 w-20 object-contain md:h-28 md:w-28"
        />
      </button>
    </>
  );
}

// The EVERHAIR video in the server HTML (Google video indexing), over the hero
// photo with the photo as its poster. The play button still opens the player.
function HeroVideo() {
  return (
    <PosterVideo
      src={HERO_VIDEO}
      poster={HERO_BG}
      title="EVERHAIR Non-Surgical Hair Growth Program"
      fit="cover"
      className="object-[center_top]"
    />
  );
}

function NonSurgicalPage() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section
          className="relative bg-[#f4f4f4] md:min-h-[620px]"
          data-publish-image-check="non-surgical-hero"
        >
          {/* Desktop right-side image */}
          <div
            className="absolute inset-y-0 right-0 hidden w-1/2 bg-cover bg-[position:center_top] md:block"
            style={{ backgroundImage: `url(${HERO_BG})` }}
          >
            <HeroVideo />
          </div>

          {/* Mobile stacked layout: image on top, text below */}
          <div className="md:hidden">
            <div
              className="relative h-[360px] w-full bg-cover bg-[position:center_top]"
              style={{ backgroundImage: `url(${HERO_BG})` }}
            >
              <HeroVideo />
            </div>
            <div className="px-4 py-8">
              <HeroContent setVideoOpen={setVideoOpen} />
            </div>
          </div>

          {/* Desktop text layout */}
          <div className="relative mx-auto hidden w-full max-w-6xl items-center px-4 py-12 md:flex md:min-h-[620px] lg:px-8">
            <div className="max-w-xl md:w-1/2">
              <HeroContent setVideoOpen={setVideoOpen} headingTag="h1" />
            </div>
          </div>
        </section>

        {videoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/80 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setVideoOpen(false)}
          >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setVideoOpen(false)}
                aria-label="Close video"
                className="absolute -top-10 right-0 text-2xl font-bold text-background"
              >
                ×
              </button>
              <video src={HERO_VIDEO} controls autoPlay className="w-full" />
            </div>
          </div>
        )}

        <section className="py-12 md:py-16">

          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <p className="text-base leading-relaxed text-muted-foreground">
              If you're worried about thinning hair but aren't ready for a hair restoration
              procedure, <span className="font-bold text-foreground">EVERHAIR</span> is the solution
              for you.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              EVERHAIR is a complete hair growth program designed to help prevent future hair loss
              while supporting healthier, fuller-looking hair. The program combines doctor-approved
              low-level laser therapy (LLLT), prescription-strength medications, specialized shampoo,
              and ongoing physician monitoring for a personalized approach to hair restoration.
              Whether you're looking for a non-surgical solution, added privacy, or an easy way to
              maintain your current style, EVERHAIR offers effective options tailored to different
              hair types, including curly hair and textured hair.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              It's an effective non-surgical hair regrowth solution that is quick, easy, and
              affordable, helping reduce hair loss and promote fuller, healthier-looking hair for a
              more youthful appearance.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-8 px-4 sm:grid-cols-3 lg:px-8">
            {benefits.map((b) => (
              <div key={b.label} className="text-center">
                <img src={b.icon} alt={b.alt} loading="lazy" className="mx-auto h-20 w-20 object-contain" />
                <p className="mt-4 text-sm font-bold uppercase tracking-wide text-foreground">
                  {b.label}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <div className="grid grid-cols-2 gap-6">
              <figure>
                <img
                  src={BEFORE_IMG}
                  alt="Non surgical hair solution before photo"
                  loading="lazy"
                  className="h-auto w-full object-cover"
                />
                <figcaption className="mt-2 text-center text-sm font-bold uppercase text-foreground">
                  Before
                </figcaption>
              </figure>
              <figure>
                <img
                  src={AFTER_IMG}
                  alt="Non surgical hair solution after photo"
                  loading="lazy"
                  className="h-auto w-full object-cover"
                />
                <figcaption className="mt-2 text-center text-sm font-bold uppercase text-foreground">
                  After
                </figcaption>
              </figure>
            </div>
            <p className="mt-8 text-center text-sm font-bold text-foreground">
              Matt D. – Age 32, EverHair Complete Package
            </p>
            <p className="mt-2 text-center text-base italic text-muted-foreground">
              "I'm so blown away with my results, and the treatment was beyond easy."
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center text-3xl font-extrabold text-foreground md:text-4xl">
              A Complete Hair Growth Program
            </h2>
            <div className="mt-10 grid gap-10 md:grid-cols-3">
              {program.map((p) => (
                <div key={p.title} className="text-center">
                  <img src={p.icon} alt={p.alt} loading="lazy" className="mx-auto h-20 w-20 object-contain" />
                  <h3 className="mt-5 text-lg font-bold text-foreground">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Faq items={nonSurgicalFaqs.length ? nonSurgicalFaqs : undefined} />
        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}